import React from "react";
import { Search, Sparkles, FileText, Calendar } from "lucide-react";
import { useChatContext } from "./providers/ChatProvider";
import { CHAT_COLUMN_MAX_WIDTH } from "./layout";
import { cn } from "@/lib/cn";

/** Suggested prompts on the empty screen. Clicking prefills the composer.
 *  One card design for every preset (icon tile + title + sub) — the card
 *  surface, borders, and text recolor per theme via semantic tokens. */
const STARTERS: { icon: React.ReactNode; title: string; sub: string; prompt: string; ph: string }[] = [
  { ph: "select_search", icon: <Search size={19} />, title: "Search my knowledge", sub: "Find & summarize what you've saved",
    prompt: "Search my knowledge base and summarize what you find about " },
  { ph: "select_skill", icon: <Sparkles size={19} />, title: "Run a skill", sub: "See what your skills can do",
    prompt: "What skills can you run for me? Give me a few examples of how each could help." },
  { ph: "select_summary", icon: <FileText size={19} />, title: "Summarize a document", sub: "Key points & action items",
    prompt: "Summarize this document for me and pull out the key action items: " },
  { ph: "select_reminder", icon: <Calendar size={19} />, title: "Set up a reminder", sub: "Schedule a nudge for later",
    prompt: "Help me set up a reminder. I want to be reminded to " },
];

export function ConversationStarters() {
  const { setComposerDraft } = useChatContext();

  return (
    /* Two columns only when the chat column itself is wide enough (`@md` =
       448px of container width) — a viewport breakpoint would give a narrow
       embed panel two cramped columns just because the host page is wide. */
    <div
      data-ph-scope="starters"
      className="grid w-full grid-cols-1 gap-3 @md:grid-cols-2 mt-5 font-sans"
      style={{ maxWidth: CHAT_COLUMN_MAX_WIDTH }}
    >
      {STARTERS.map((s) => (
        <button
          key={s.title}
          data-ph={s.ph}
          type="button"
          onClick={() => setComposerDraft(s.prompt)}
          className={cn(
            "flex items-center gap-3 text-left cursor-pointer rounded-lg bg-nav-active",
            "border border-transparent p-3.5 transition-colors hover:bg-[color-mix(in_srgb,var(--color-accent)_14%,transparent)]",
          )}
        >
          <span
            className={cn(
              "grid h-9 w-9 shrink-0 place-items-center rounded-md",
              "bg-accent/10 text-accent",
            )}
          >
            {s.icon}
          </span>
          <span className="min-w-0">
            <span className="block text-[13.5px] font-bold text-ink truncate">{s.title}</span>
            <span className="block text-[12.5px] text-ink-muted truncate">{s.sub}</span>
          </span>
        </button>
      ))}
    </div>
  );
}
